// @ts-check
import {FileUpload, SectionTitle, TextArea, TextInput} from '@/components-v2'
import {useProductCombo} from '@/context/ProductCombo/ProductComboContext'
import {convertImageToWebP, sanitizeFileName} from '@/helpers/shadcn/utils'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {uploadProductMedia} from '@/services/ProductsService/product'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {COMBO_LIMIT_MAX, isNumberWithinRange, PRODUCT_LIMIT_MIN} from '@/utils/productCombo/productComboValidation'
import {useEffect, useState} from 'react'

const mockLanguages = [
  {code: 'en', language: 'English'},
  {code: 'es-MX', language: 'Spanish (Mexico)'},
]

export const ProductComboDisplay = () => {
  const {productCombo, updateProductCombo} = useProductCombo()
  const languagesFromHook = useFeatureFlagLanguages()
  const dispatch = useAppDispatch()
  const {productLimit} = productCombo
  const [productLimitInput, setProductLimitInput] = useState(
    productLimit === null || productLimit === undefined ? '' : String(productLimit)
  )
  const [hasInvalidProductLimitDraft, setHasInvalidProductLimitDraft] = useState(false)
  const productLimitError = productLimitInput
    ? isNumberWithinRange(productLimitInput, PRODUCT_LIMIT_MIN, COMBO_LIMIT_MAX)
    : ''

  const languages = languagesFromHook && languagesFromHook.length > 0 ? languagesFromHook : mockLanguages

  useEffect(() => {
    if (hasInvalidProductLimitDraft) {
      return
    }

    setProductLimitInput(productLimit === null || productLimit === undefined ? '' : String(productLimit))
  }, [hasInvalidProductLimitDraft, productLimit])

  const handleTextChange = (value, language, field) => {
    updateProductCombo({
      [field]: {
        ...productCombo[field],
        [language]: value,
      },
    })
  }

  const handleLimitChange = value => {
    setProductLimitInput(value)

    if (value === '') {
      setHasInvalidProductLimitDraft(false)
      updateProductCombo({productLimit: null})
      return
    }

    if (/^[+-]?\d+$/.test(value.trim())) {
      setHasInvalidProductLimitDraft(false)
      updateProductCombo({productLimit: Number(value.trim())})
      return
    }

    setHasInvalidProductLimitDraft(true)
    updateProductCombo({productLimit: null})
  }

  const handleComboImageChange = async file => {
    if (!file) {
      updateProductCombo({imageUrl: ''})
      return
    }

    const sanitizedFile = new File([file], sanitizeFileName(file.name), {
      type: file.type,
      lastModified: file.lastModified,
    })
    const {webpFile} = await convertImageToWebP(sanitizedFile)
    const processedFile = webpFile ?? sanitizedFile

    // Create a local preview so FileUpload receives a new value immediately
    const previewUrl = URL.createObjectURL(processedFile)
    updateProductCombo({imageUrl: previewUrl})

    if (!productCombo?.productId) {
      console.error('No Combo Product id is available for uploading media.')
      return
    }

    const metadata = {
      mediaUploadMetadata: [
        {
          id: productCombo?.productId,
          originalFileName: processedFile.name,
          originalFormat: processedFile.type.split('/')[1],
          originalSize: processedFile.size,
          blobFileSize: processedFile.size,
          blobFormat: processedFile.type.split('/')[1],
          referenceEntity: 'productCombo',
        },
      ],
    }

    const formData = new FormData()
    formData.append('image', processedFile)
    formData.append('metadata', JSON.stringify(metadata))

    try {
      const response = await uploadProductMedia(productCombo.productId, formData)
      const comboImageUrl = response?.data?.[0]?.imageCDNUrl
      updateProductCombo({imageUrl: comboImageUrl})
    } catch (error) {
      let errorMessage = 'Error uploading image'

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.data?.message) {
        errorMessage = error.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      // Check if the error is related to filename issues
      if (
        errorMessage.toLowerCase().includes('filename') ||
        errorMessage.toLowerCase().includes('name') ||
        errorMessage.toLowerCase().includes('invalid')
      ) {
        errorMessage =
          'Filename contains special characters that are not supported. Please rename your file to use only letters, numbers, hyphens, and underscores.'
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: errorMessage,
          type: 'error',
        })
      )
      updateProductCombo({imageUrl: ''})
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 rounded-lg bg-[var(--color-background-layer-01)] p-6">
        <SectionTitle label={productCombo?.name || ''} />
        <div className="h-px bg-[var(--secondary-800)]" />
        <h3 className="my-4 font-medium">Combo Image</h3>

        <FileUpload
          type="image"
          label="Combo Image*"
          imageUri={productCombo?.imageUrl}
          onUpdate={handleComboImageChange}
          onDelete={() => handleComboImageChange(null)}
          accept=".png,.jpg,.jpeg,.webp"
          className="min-h-40 w-96"
        />

        <div className="h-px bg-[var(--secondary-800)]" />

        <TextInput
          label="Product Limit*"
          value={productLimitInput}
          onUpdate={handleLimitChange}
          error={productLimitError || undefined}
          placeholderText="Product Limit"
          className="w-64"
        />
      </div>

      <div className="flex flex-col gap-4 rounded-lg bg-[var(--color-background-layer-01)] p-4">
        <SectionTitle label="Combo Text" />
        <div className="h-px bg-[var(--secondary-800)]" />
        <div className="grid grid-cols-3 gap-4">
          {languages.map(lang => {
            const languageName = lang.language
            const language = lang.code

            return (
              <div key={language} className="mt-6 space-y-5 font-semibold">
                <h3 className="font-semibold">{languageName}</h3>
                <TextInput
                  feedbackType="info"
                  inputSize="medium"
                  label="Combo Subtitle*"
                  className="combo-text-input"
                  onUpdate={value => handleTextChange(value, language, 'comboSubtitle')}
                  placeholderText="Enter subtitle"
                  value={productCombo?.comboSubtitle?.[language] || ''}
                />
                <TextInput
                  feedbackType="info"
                  inputSize="medium"
                  label="Keywords"
                  className="combo-text-input"
                  onUpdate={value => handleTextChange(value, language, 'keywords')}
                  placeholderText="Enter keywords"
                  value={productCombo?.keywords?.[language] || ''}
                />
                <TextArea
                  label="Combo Details*"
                  className="combo-text-input description"
                  maxLength={500}
                  onUpdate={value => handleTextChange(value, language, 'comboDetails')}
                  value={productCombo?.comboDetails?.[language] || ''}
                  placeholder="Type here"
                  showToolbar={false}
                  showCharacterCount={true}
                />
                <TextArea
                  label="Combo Details 2"
                  className="combo-text-input description"
                  maxLength={500}
                  onUpdate={value => handleTextChange(value, language, 'comboDetails2')}
                  value={productCombo?.comboDetails2?.[language] || ''}
                  placeholder="Type here"
                  showToolbar={false}
                  showCharacterCount={true}
                />
                <TextArea
                  label="Description*"
                  className="combo-text-input description"
                  maxLength={500}
                  onUpdate={value => handleTextChange(value, language, 'comboDescription')}
                  value={productCombo?.comboDescription?.[language] || ''}
                  placeholder="Type here"
                  showToolbar={false}
                  showCharacterCount={true}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
